import React from 'react';
import sharedStyles from '../AdminShared.module.scss';
import styles from '../AdminAnalytics.module.scss';
import { AnalyticsPlaybackPerformance } from '../../../../types/analytics.types';

interface PlaybackPerformancePanelProps {
    performance?: AnalyticsPlaybackPerformance;
    rangeLabel?: string;
    title?: string;
}

const emptyPerformance: AnalyticsPlaybackPerformance = {
    startupSampleCount: 0,
    averageStartupMs: 0,
    bufferEventCount: 0,
    averageBufferMs: 0,
    seekSampleCount: 0,
    averageSeekMs: 0,
    playbackErrorCount: 0,
    buckets: [],
    videos: []
};

const formatNumber = (value: number) => value.toLocaleString();
const formatMilliseconds = (value: number) => `${Math.round(value).toLocaleString()} ms`;

/**
 * Reusable playback performance dashboard panel for admin and SuperAdministrator analysis views.
 */
const PlaybackPerformancePanel = ({
    performance = emptyPerformance,
    rangeLabel,
    title = 'Playback Performance'
}: PlaybackPerformancePanelProps) => {
    const buckets = performance.buckets ?? [];

    return (
        <section className={sharedStyles.panel}>
            <div className={styles.panelHeading}>
                <div>
                    <h2>{title}</h2>
                    {rangeLabel ? <p className={styles.rangeLabel}>{rangeLabel}</p> : null}
                </div>
            </div>
            <div className={styles.metricGrid}>
                <div className={styles.metricItem}>
                    <span>Avg Startup</span>
                    <strong>{formatMilliseconds(performance.averageStartupMs)}</strong>
                    <small>{formatNumber(performance.startupSampleCount)} samples</small>
                </div>
                <div className={styles.metricItem}>
                    <span>Avg Buffer</span>
                    <strong>{formatMilliseconds(performance.averageBufferMs)}</strong>
                    <small>{formatNumber(performance.bufferEventCount)} events</small>
                </div>
                <div className={styles.metricItem}>
                    <span>Avg Seek Recovery</span>
                    <strong>{formatMilliseconds(performance.averageSeekMs)}</strong>
                    <small>{formatNumber(performance.seekSampleCount)} samples</small>
                </div>
                <div className={styles.metricItem}>
                    <span>Playback Errors</span>
                    <strong>{formatNumber(performance.playbackErrorCount)}</strong>
                    <small>media errors</small>
                </div>
            </div>
            {buckets.length === 0 ? (
                <p className={sharedStyles.muted}>No playback performance samples in this range.</p>
            ) : (
                <div className={styles.tableWrap}>
                    <table className={sharedStyles.table}>
                        <thead>
                            <tr>
                                <th>Quality / Transport</th>
                                <th>Startup Avg</th>
                                <th>Startup Samples</th>
                                <th>Buffer Avg</th>
                                <th>Buffer Events</th>
                                <th>Seek Avg</th>
                                <th>Seek Samples</th>
                                <th>Errors</th>
                            </tr>
                        </thead>
                        <tbody>
                            {buckets.map((bucket) => (
                                <tr key={bucket.label}>
                                    <td>{bucket.label}</td>
                                    <td>{formatMilliseconds(bucket.averageStartupMs)}</td>
                                    <td>{formatNumber(bucket.startupSampleCount)}</td>
                                    <td>{formatMilliseconds(bucket.averageBufferMs)}</td>
                                    <td>{formatNumber(bucket.bufferEventCount)}</td>
                                    <td>{formatMilliseconds(bucket.averageSeekMs)}</td>
                                    <td>{formatNumber(bucket.seekSampleCount)}</td>
                                    <td>{formatNumber(bucket.playbackErrorCount)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};

export default PlaybackPerformancePanel;
